import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type { GridColumn } from './types';

interface Layout { widths: Record<string, number>; hidden: Record<string, boolean>; order?: string[] }

/**
 * Kullanıcı grid düzeni (kolon genişliği / görünürlük).
 * Delphi Dizayn_Kaydet/Dizayn_Yukle karşılığı — sunucuda Web_Grid_Layout'a (user0.Dizayn yetkisi varsa) yazılır,
 * yetki yoksa yalnızca bu tarayıcıda saklanır.
 */
export function useGridLayout(key: string | undefined, base: GridColumn[]) {
  const [layout, setLayout] = useState<Layout>({ widths: {}, hidden: {} });
  const loaded = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    loaded.current = false;
    if (!key) return;
    let local: Layout | null = null;
    try { local = JSON.parse(localStorage.getItem('dolphin.layout.' + key) ?? 'null'); } catch { /* */ }
    if (local) setLayout(local);
    api<{ layout: Layout | null }>(`/layouts?key=${encodeURIComponent(key)}`)
      .then((r) => { if (r.layout) setLayout(r.layout); })
      .catch(() => undefined)
      .finally(() => { loaded.current = true; });
  }, [key]);

  const persist = useCallback((l: Layout) => {
    if (!key) return;
    try { localStorage.setItem('dolphin.layout.' + key, JSON.stringify(l)); } catch { /* */ }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      api('/layouts', { method: 'PUT', body: { key, layout: l } }).catch(() => undefined);
    }, 800);
  }, [key]);

  const update = (fn: (l: Layout) => Layout) => setLayout((l) => { const n = fn(l); persist(n); return n; });

  const columns = useMemo(() => base.map((c) => ({
    ...c,
    width: layout.widths[c.key] ?? c.width,
    hidden: layout.hidden[c.key] ?? c.hidden,
  })), [base, layout]);

  return {
    columns,
    setWidth: (k: string, w: number) => update((l) => ({ ...l, widths: { ...l.widths, [k]: Math.round(w) } })),
    toggleHidden: (k: string) => update((l) => {
      const cur = l.hidden[k] ?? base.find((c) => c.key === k)?.hidden ?? false;
      return { ...l, hidden: { ...l.hidden, [k]: !cur } };
    }),
    reset: () => update(() => ({ widths: {}, hidden: {} })),
  };
}
